// @ts-nocheck
import { useEffect, useMemo, useState } from 'react'
import { AgGridReact } from '@ag-grid-community/react'

import api from '../api/api'
import GraphDayDetailsModal from './GraphDayDetailsModal'
import {
  getGraphDayLabel,
  getGraphDayState,
  getReasonStripeBackground,
  hasGraphDayDetails,
} from '../utils/graphDay'


let completionSettingsCache = null
let completionSettingsPromise = null
const panelRequestPromises = new Map()

function dedupedGet(key, url, config) {
  if (panelRequestPromises.has(key)) {
    return panelRequestPromises.get(key)
  }

  const promise = api.get(url, config)
    .finally(() => {
      panelRequestPromises.delete(key)
    })

  panelRequestPromises.set(key, promise)
  return promise
}

function loadCompletionSettings() {
  if (completionSettingsCache) {
    return Promise.resolve(completionSettingsCache)
  }

  if (!completionSettingsPromise) {
    completionSettingsPromise = api.get('completion-settings/')
      .then((response) => {
        completionSettingsCache = response.data
        return response.data
      })
      .finally(() => {
        completionSettingsPromise = null
      })
  }

  return completionSettingsPromise
}

function RequestWorkPanel({ user, requests, selectedRequest, onSelectRequest, onUpdated, refreshKey }) {
  const [graphData, setGraphData] = useState(null)
  const [dayPlanRows, setDayPlanRows] = useState([])
  const [wellHistoryRows, setWellHistoryRows] = useState([])
  const [historyMonths, setHistoryMonths] = useState('5')
  const [workDate, setWorkDate] = useState(() => (
    selectedRequest?.planned_date || selectedRequest?.requested_date || ''
  ))
  const [commentModal, setCommentModal] = useState(null)
  const [bsiDetails, setBsiDetails] = useState(null)
  const [comment, setComment] = useState('')
  const [completedDate, setCompletedDate] = useState('')
  const [completionSettings, setCompletionSettings] = useState(null)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [retryingEmailId, setRetryingEmailId] = useState(null)
  const [expandedNotificationGroups, setExpandedNotificationGroups] = useState({})
  const [isReminding, setIsReminding] = useState(false)
  const closedStatuses = ['DONE', 'REJECTED', 'CANCELED']
  const canEditWorkDate = !user.is_admin && selectedRequest?.status === 'NEW'
  const canCancelRequest = user.is_admin && selectedRequest && !closedStatuses.includes(selectedRequest.status)
  const canRemindRequest = user.is_admin && selectedRequest && !closedStatuses.includes(selectedRequest.status)
  const canRecheckCompletion = user.is_admin && selectedRequest?.status === 'WAITING_CONFIRMATION'
  const canConfirmCompletion = user.is_admin && selectedRequest?.status === 'WAITING_CONFIRMATION'
  const selectedRequestId = selectedRequest?.id
  const selectedRequestStatus = selectedRequest?.status
  const selectedWellId = selectedRequest?.well
  const selectedOrgId = selectedRequest?.org
  const selectedRequestWorkDate = selectedRequest?.planned_date || selectedRequest?.requested_date || ''

  useEffect(() => {
    if (!selectedRequestId) {
      setWorkDate('')
      return
    }

    if (user.is_admin || selectedRequestStatus !== 'NEW') {
      setWorkDate(selectedRequestWorkDate)
    }
  }, [selectedRequestId, selectedRequestWorkDate, selectedRequestStatus, user.is_admin])

  useEffect(() => {
    if (!selectedWellId || !selectedOrgId) {
      return
    }

    const requestKey = `well-current-graph:${selectedWellId}:${selectedOrgId}:${workDate || ''}:${refreshKey}`

    dedupedGet(requestKey, 'well-current-graph/', {
      params: {
        well: selectedWellId,
        org: selectedOrgId,
        date: workDate || undefined,
      },
    })
      .then((response) => setGraphData(response.data))
      .catch((requestError) => {
        console.error(requestError)
        setGraphData(null)
      })
  }, [selectedWellId, selectedOrgId, workDate, refreshKey])

  useEffect(() => {
    if (!selectedOrgId || !workDate) {
      return
    }

    const requestKey = `org-day-plan:${selectedOrgId}:${workDate}:${refreshKey}`

    dedupedGet(requestKey, 'org-day-plan/', {
      params: {
        date: workDate,
        org: selectedOrgId,
      },
    })
      .then((response) => setDayPlanRows(response.data.results || []))
      .catch((requestError) => {
        console.error(requestError)
        setDayPlanRows([])
      })
  }, [selectedOrgId, workDate, refreshKey])

  useEffect(() => {
    if (!selectedWellId || !selectedOrgId) {
      return
    }

    const requestKey = `well-history:${selectedWellId}:${selectedOrgId}:${historyMonths}:${refreshKey}`

    dedupedGet(requestKey, 'well-history/', {
      params: {
        well: selectedWellId,
        org: selectedOrgId,
        months: historyMonths,
      },
    })
      .then((response) => setWellHistoryRows(response.data.results || []))
      .catch((requestError) => {
        console.error(requestError)
        setWellHistoryRows([])
      })
  }, [selectedWellId, selectedOrgId, historyMonths, refreshKey])

  useEffect(() => {
    loadCompletionSettings()
      .then((data) => setCompletionSettings(data))
      .catch((requestError) => console.error(requestError))
  }, [])

  const monthInfo = useMemo(() => {
    if (!graphData?.found) {
      return null
    }

    let planCount = 0
    let passCount = 0

    for (let day = 1; day <= 31; day += 1) {
      const value = graphData.graph.days[String(day)]

      if (!value || typeof value !== 'object') {
        continue
      }

      if (String(value.plan) === '1') {
        planCount += 1
      }

      if (String(value.plan) === '2') {
        planCount += 1
      }

      const dayState = getGraphDayState(value)

      if (['done_planned', 'done_outside_plan', 'done'].includes(dayState.kind)) {
        passCount += dayState.bsiItems.length
      }

      if (['manual_planned', 'manual_outside_plan'].includes(dayState.kind)) {
        passCount += 1
      }
    }

    return {
      planCount,
      passCount,
    }
  }, [graphData])

  const selectedDatePlanCount = useMemo(() => (
    dayPlanRows.reduce((sum, row) => sum + Number(row.planned_passes || 0), 0)
  ), [dayPlanRows])
  const graphMonthLabel = useMemo(() => formatMonthLabel(workDate), [workDate])
  const filteredDayPlanRows = useMemo(() => (
    dayPlanRows
      .sort((a, b) => (
        String(a.bush_name || '').localeCompare(String(b.bush_name || ''), 'ru')
        || String(a.well_name || '').localeCompare(String(b.well_name || ''), 'ru')
      ))
  ), [dayPlanRows])
  const notificationGroups = useMemo(() => (
    groupEmailNotifications(selectedRequest?.email_notifications || [])
  ), [selectedRequest?.email_notifications])

  const columnDefs = useMemo(() => [
    {
      field: 'date',
      headerName: 'Дата графика',
      width: 130,
    },
    {
      field: 'deposit_name',
      headerName: 'Месторождение',
      width: 180,
    },
    {
      field: 'bush_name',
      headerName: 'Куст',
      width: 110,
    },
    {
      field: 'well_name',
      headerName: 'Скважина',
      width: 120,
    },
  ], [])
  const historyColumnDefs = useMemo(() => [
    {
      field: 'month',
      headerName: 'Месяц',
      width: 82,
      valueFormatter: (params) => formatMonthShort(params.value),
    },
    {
      field: 'bush_name',
      headerName: 'Куст',
      width: 62,
    },
    {
      field: 'well_name',
      headerName: 'Скв.',
      width: 74,
    },
    {
      field: 'plan_fact',
      headerName: 'План / факт',
      width: 84,
      valueGetter: (params) => `${Number(params.data?.plan || 0)} / ${Number(params.data?.fact || 0)}`,
    },
    {
      field: 'level',
      headerName: 'Степень',
      width: 90,
    },
    ...Array.from({ length: 31 }, (_, index) => {
      const day = index + 1

      return {
        headerName: String(day),
        field: `days.${day}`,
        width: 30,
        minWidth: 30,
        maxWidth: 30,
        headerClass: 'graph-day-header',
        cellClass: 'graph-day-cell',
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        suppressMenu: true,
        menuTabs: [],
        filter: false,
        sortable: false,
        valueFormatter: (params) => {
          return getGraphDayLabel(params.data?.days?.[String(day)])
        },
        cellStyle: (params) => historyDayCellStyle(
          params.data?.days?.[String(day)],
          day,
          params.data?.days_in_month,
        ),
        onCellClicked: (params) => {
          if (params.data?.days_in_month && day > params.data.days_in_month) {
            return
          }

          const value = params.data?.days?.[String(day)]
          if (!hasGraphDayDetails(value)) {
            return
          }

          setBsiDetails({
            month: params.data.month,
            day,
            wellName: params.data.well_name,
            value,
          })
        },
      }
    }),
  ], [])

  function openCommentModal(action) {
    setError('')
    setComment('')
    setCompletedDate(['recheck', 'confirm'].includes(action) && selectedRequest?.reported_completed_date
      ? selectedRequest.reported_completed_date
      : getTodayIso())
    setCommentModal(action)
  }

  async function submitComment(event) {
    event.preventDefault()
    setError('')

    if (isSubmitting) {
      return
    }

    if (!['recheck', 'confirm'].includes(commentModal) && !comment.trim()) {
      setError('Введите комментарий')
      return
    }

    if (['complete', 'recheck', 'confirm'].includes(commentModal) && !completedDate) {
      setError('Выберите дату выполнения')
      return
    }

    const endpoint = commentModal === 'accept'
      ? `requests/${selectedRequest.id}/accept/`
      : commentModal === 'complete'
        ? `requests/${selectedRequest.id}/complete/`
        : commentModal === 'recheck'
          ? `requests/${selectedRequest.id}/recheck-completion/`
          : commentModal === 'confirm'
            ? `requests/${selectedRequest.id}/confirm-completion/`
            : commentModal === 'cancel'
              ? `requests/${selectedRequest.id}/cancel/`
              : `requests/${selectedRequest.id}/reject/`

    setIsSubmitting(true)

    try {
      await api.post(endpoint, {
        comment,
        planned_date: workDate || undefined,
        completed_date: ['complete', 'recheck', 'confirm'].includes(commentModal) ? completedDate : undefined,
      })

      setCommentModal(null)
      await onUpdated()
    } catch (requestError) {
      const detail = requestError.response?.data?.detail
      const commentError = requestError.response?.data?.comment
      const completedDateError = requestError.response?.data?.completed_date
      setError(detail || commentError || completedDateError || 'Не удалось сохранить действие')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function retryEmail(notificationId) {
    if (!selectedRequest || retryingEmailId) {
      return
    }

    setRetryingEmailId(notificationId)

    try {
      await api.post(`requests/${selectedRequest.id}/retry-email/`, {
        notification_id: notificationId,
      })
      await onUpdated()
    } catch (requestError) {
      console.error(requestError.response?.data || requestError)
    } finally {
      setRetryingEmailId(null)
    }
  }

  async function sendReminder() {
    if (!selectedRequest || isReminding) {
      return
    }

    setIsReminding(true)
    setError('')

    try {
      await api.post(`requests/${selectedRequest.id}/remind/`)
      await onUpdated()
    } catch (requestError) {
      const detail = requestError.response?.data?.detail
      setError(detail || 'Не удалось отправить напоминание')
    } finally {
      setIsReminding(false)
    }
  }

  if (!selectedRequest) {
    return (
      <div style={panelStyle}>
        Выберите заявку в таблице, чтобы посмотреть график и действия подрядчика.
      </div>
    )
  }

  return (
    <div style={panelStyle}>
      <style>{agPopupStyle}</style>
      <div style={headerStyle}>
        <div>
          <div style={titleStyle}>
            {selectedRequest.display_number} / скв. {selectedRequest.well_name} / {selectedRequest.org_name}
          </div>
          <div style={mutedStyle}>
            Запрошено админом: {formatDate(selectedRequest.requested_date)}
          </div>
          {(selectedRequest.location_display || selectedRequest.requested_by_name) && (
            <div style={mutedStyle}>
              Локация: {selectedRequest.location_display || '—'} / ДНС: {selectedRequest.dns_name || '—'} / Кто затребовал: {selectedRequest.requested_by_name || '—'}
            </div>
          )}
          <div style={mutedStyle}>
            Статус: {selectedRequest.status_display || selectedRequest.status}
          </div>
        </div>

        {user.is_admin && (
          <div style={actionsStyle}>
            {canRemindRequest && (
              <button type="button" style={secondaryButtonStyle} onClick={sendReminder} disabled={isReminding}>
                {isReminding ? 'Отправляем...' : 'Напомнить'}
              </button>
            )}
            {canRecheckCompletion && (
              <button type="button" style={primaryButtonStyle} onClick={() => openCommentModal('recheck')} disabled={isSubmitting}>
                Проверить другую дату
              </button>
            )}
            {canConfirmCompletion && (
              <button type="button" style={secondaryButtonStyle} onClick={() => openCommentModal('confirm')} disabled={isSubmitting}>
                Закрыть вручную
              </button>
            )}
            {canCancelRequest && (
              <button type="button" style={dangerButtonStyle} onClick={() => openCommentModal('cancel')} disabled={isSubmitting}>
                Отозвать контрольный спуск
              </button>
            )}
          </div>
        )}

        {!user.is_admin && (
          <div style={actionsStyle}>
            {selectedRequest.status === 'NEW' && (
              <>
                <button type="button" style={primaryButtonStyle} onClick={() => openCommentModal('accept')} disabled={isSubmitting}>
                  Взять в работу
                </button>
                <button type="button" style={dangerButtonStyle} onClick={() => openCommentModal('reject')} disabled={isSubmitting}>
                  Отклонить
                </button>
              </>
            )}

            {['ACCEPTED', 'PLANNED'].includes(selectedRequest.status) && (
              <>
                <button type="button" style={primaryButtonStyle} onClick={() => openCommentModal('complete')} disabled={isSubmitting}>
                  Выполнена
                </button>
                <button type="button" style={disabledButtonStyle} disabled>
                  Отклонить
                </button>
              </>
            )}

            {selectedRequest.status === 'DONE' && (
              <span style={doneTextStyle}>Заявка выполнена</span>
            )}

            {selectedRequest.status === 'WAITING_CONFIRMATION' && (
              <span style={waitingTextStyle}>Ожидает подтверждения</span>
            )}

            {selectedRequest.status === 'REJECTED' && (
              <span style={rejectedTextStyle}>Заявка отклонена</span>
            )}

            {selectedRequest.status === 'CANCELED' && (
              <span style={rejectedTextStyle}>Контрольный спуск отозван</span>
            )}
          </div>
        )}
      </div>

      <div style={controlsStyle}>
        <label style={labelStyle}>
          Дата подрядчика
          <input
            type="date"
            value={workDate}
            onChange={(event) => setWorkDate(event.target.value)}
            style={inputStyle}
            disabled={!canEditWorkDate}
          />
        </label>
      </div>

      <div style={summaryStyle}>
        {workDate && (
          <span>
            На выбранную дату - запланировано {selectedDatePlanCount} проходок
          </span>
        )}
        {!workDate && 'Выберите дату, чтобы увидеть план подрядчика.'}
        {graphData && !graphData.found && (
          <span>
            Скважина не участвует в актуальном графике этой организации.
          </span>
        )}
      </div>

      {monthInfo && (
        <div style={monthSummaryStyle}>
          <span>Итого за месяц по выбранной скважине</span>
          <span>План: {monthInfo.planCount}</span>
          <span>Проходок БСИ: {monthInfo.passCount}</span>
        </div>
      )}

      {selectedRequest.completion_confirmation && (
        <div style={confirmationStyle(selectedRequest.completion_confirmation.confirmed)}>
          <strong>Подтверждение выполнения КС:</strong>
          {' '}
          {selectedRequest.completion_confirmation.reason || '—'}
          {selectedRequest.completion_confirmation.depth ? ` / Глубина: ${selectedRequest.completion_confirmation.depth} м` : ''}
          {selectedRequest.completion_confirmation.bsi_level ? ` / Степень: ${selectedRequest.completion_confirmation.bsi_level}` : ''}
        </div>
      )}

      {selectedRequest.comments?.length > 0 && (
        <div style={commentsStyle}>
          {selectedRequest.comments.map((item) => (
            <div key={item.id}>
              <span style={commentDateStyle}>{formatDateTime(item.created_at)}</span>
              {' '}
              <strong>{item.author_name}</strong>: {item.text}
            </div>
          ))}
        </div>
      )}

      <div style={splitTablesStyle}>
        <div style={splitTableStyle}>
          <div style={splitTableTitleStyle}>
            <div style={sectionTitleStyle}>
              Плановые скважины подрядчика на выбранную дату из графика скребкования на {graphMonthLabel}
            </div>
          </div>
          <div className="ag-theme-alpine work-grid day-plan-grid" style={dayPlanGridStyle}>
            <AgGridReact
                rowData={filteredDayPlanRows}
                columnDefs={columnDefs}
                rowHeight={26}
                headerHeight={28}
                suppressMenuHide={true}
                getRowId={(params) => params.data.id}
                onRowClicked={(params) => {
                  const matchedRequest = requests.find((request) => (
                    request.well === params.data.well
                  ))

                  if (matchedRequest) {
                    onSelectRequest(matchedRequest)
                  }
                }}
                defaultColDef={tableDefaultColDef}
              />
          </div>
        </div>

        <div style={splitTableStyle}>
          <div style={historyHeaderStyle}>
            <div style={sectionTitleStyle}>
              График выбранной скважины
            </div>
            <select
              value={historyMonths}
              onChange={(event) => setHistoryMonths(event.target.value)}
              style={historySelectStyle}
            >
              <option value="5">5 мес.</option>
              <option value="12">12 мес.</option>
              <option value="24">24 мес.</option>
              <option value="all">Все</option>
            </select>
          </div>
          <div className="ag-theme-alpine work-grid well-history-grid" style={{ height: 260, width: '100%' }}>
            <AgGridReact
                rowData={wellHistoryRows}
                columnDefs={historyColumnDefs}
                rowHeight={26}
                headerHeight={28}
                suppressMenuHide={true}
                getRowId={(params) => params.data.id}
                defaultColDef={historyDefaultColDef}
              />
          </div>
        </div>
      </div>

      {notificationGroups.length > 0 && (
        <div style={notificationsStyle}>
          <div style={sectionTitleInlineStyle}>Уведомления</div>
          <div style={notificationsTableStyle}>
            <div style={notificationHeaderStyle}>Дата</div>
            <div style={notificationHeaderStyle}>Событие</div>
            <div style={notificationHeaderStyle}>Кому</div>
            <div style={notificationHeaderStyle}>Статус</div>
            <div style={notificationHeaderStyle}>Действие</div>
            {notificationGroups.map((group) => (
              <div key={group.key} style={notificationRowStyle}>
                <div style={notificationCellStyle}>{formatDateTime(group.date)}</div>
                <div style={notificationCellStyle}>{group.eventLabel}</div>
                <div style={notificationCellStyle}>
                  <div>{formatNotificationRecipientSummary(group)}</div>
                  {expandedNotificationGroups[group.key] && (
                    <div style={notificationDetailsStyle}>
                      {group.items.map((item) => (
                        <div key={item.id} style={notificationDetailRowStyle}>
                          <div>
                            {item.recipient_email}
                            {item.original_recipient_name && (
                              <div style={notificationMutedStyle}>
                                исходно: {item.original_recipient_name}
                                {item.original_recipient_email ? ` <${item.original_recipient_email}>` : ''}
                              </div>
                            )}
                            {item.last_error && (
                              <div style={notificationErrorStyle}>{item.last_error}</div>
                            )}
                          </div>
                          {user.is_admin && item.status === 'ERROR' && (
                            <button
                              type="button"
                              style={smallButtonStyle}
                              onClick={() => retryEmail(item.id)}
                              disabled={retryingEmailId === item.id}
                            >
                              {retryingEmailId === item.id ? 'Отправляем...' : 'Повторить'}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={notificationCellStyle}>
                  <span style={notificationStatusStyle(group.status)}>
                    {group.statusLabel}
                  </span>
                  {group.errorCount > 0 && (
                    <div style={notificationErrorStyle}>Ошибок: {group.errorCount}</div>
                  )}
                </div>
                <div style={notificationCellStyle}>
                  <button
                    type="button"
                    style={smallButtonStyle}
                    onClick={() => setExpandedNotificationGroups((prev) => ({
                      ...prev,
                      [group.key]: !prev[group.key],
                    }))}
                  >
                    {expandedNotificationGroups[group.key] ? 'Скрыть' : 'Показать'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {commentModal && (
        <div style={modalOverlayStyle}>
          <form onSubmit={submitComment} style={modalStyle}>
            <div style={titleStyle}>
              {getCommentTitle(commentModal)}
            </div>
            <textarea
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              style={textareaStyle}
              placeholder="Комментарий"
            />
            {['complete', 'recheck', 'confirm'].includes(commentModal) && (
              <label style={modalLabelStyle}>
                {commentModal === 'recheck' ? 'Дата проверки' : 'Дата выполнения'}
                <input
                  type="date"
                  value={completedDate}
                  max={completionSettings?.max_completed_date || undefined}
                  onChange={(event) => setCompletedDate(event.target.value)}
                  style={modalInputStyle}
                  required
                />
                {completionSettings && (
                  <span style={modalHintStyle}>
                    Глобально открыто с {completionSettings.min_completed_date} по {completionSettings.max_completed_date}.
                    {' '}Более ранние даты доступны при персональном доступе.
                  </span>
                )}
              </label>
            )}
            {error && <div style={errorStyle}>{error}</div>}
            <div style={modalActionsStyle}>
              <button type="button" style={secondaryButtonStyle} onClick={() => setCommentModal(null)} disabled={isSubmitting}>
                Отмена
              </button>
              <button type="submit" style={primaryButtonStyle} disabled={isSubmitting}>
                {isSubmitting ? 'Сохраняем...' : 'Сохранить'}
              </button>
            </div>
          </form>
        </div>
      )}

      <GraphDayDetailsModal
        details={bsiDetails ? {
          ...bsiDetails,
          monthLabel: formatMonthShort(bsiDetails.month),
        } : null}
        onClose={() => setBsiDetails(null)}
      />
    </div>
  )
}


function formatDate(value) {
  if (!value) return '—'
  return String(value).slice(0, 10)
}

function formatDateTime(value) {
  if (!value) return ''

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function getCommentTitle(action) {
  if (action === 'accept') return 'Взять в работу'
  if (action === 'complete') return 'Отметить выполненной'
  if (action === 'recheck') return 'Проверить другую дату'
  if (action === 'confirm') return 'Закрыть заявку вручную'
  if (action === 'cancel') return 'Отозвать контрольный спуск'
  return 'Отклонить заявку'
}

function formatMonthLabel(value) {
  if (!value) return 'выбранный месяц'

  const date = new Date(`${value.slice(0, 7)}-01T00:00:00`)

  return new Intl.DateTimeFormat('ru-RU', {
    month: 'long',
    year: 'numeric',
  }).format(date)
}

function formatMonthShort(value) {
  if (!value) return '—'

  const date = new Date(`${String(value).slice(0, 7)}-01T00:00:00`)

  return new Intl.DateTimeFormat('ru-RU', {
    month: 'short',
    year: '2-digit',
  }).format(date)
}

function historyDayCellStyle(value, day, daysInMonth) {
  const baseStyle = {
    textAlign: 'center',
    border: '1px solid #e0e0e0',
  }

  if (daysInMonth && day > daysInMonth) {
    return {
      ...baseStyle,
      background: '#9ca3af',
      border: '1px solid #858b95',
    }
  }

  if (!value || typeof value !== 'object') {
    return {
      ...baseStyle,
      background: '#ffffff',
    }
  }

  const dayState = getGraphDayState(value)

  if (dayState.kind === 'done_planned' || dayState.kind === 'done') {
    return {
      ...baseStyle,
      background: '#81c784',
      border: '1px solid #66bb6a',
      cursor: 'pointer',
    }
  }

  if (dayState.kind === 'manual_planned') {
    return {
      ...baseStyle,
      background: '#2e7d32',
      border: '1px solid #1b5e20',
      cursor: 'pointer',
    }
  }

  if (dayState.kind === 'manual_outside_plan') {
    return {
      ...baseStyle,
      background: 'linear-gradient(to bottom right, #2e7d32 0 calc(50% - 0.5px), #ffffff calc(50% + 0.5px) 100%)',
      border: '1px solid #1b5e20',
      cursor: 'pointer',
    }
  }

  if (dayState.kind === 'done_outside_plan') {
    return {
      ...baseStyle,
      background: 'linear-gradient(to bottom right, #81c784 0 calc(50% - 0.5px), #ffffff calc(50% + 0.5px) 100%)',
      border: '1px solid #66bb6a',
      cursor: 'pointer',
    }
  }

  if (dayState.kind === 'ppk_pending') {
    return {
      ...baseStyle,
      background: 'linear-gradient(to bottom right, #fff176 0 calc(50% - 0.5px), #66bb6a calc(50% + 0.5px) 100%)',
      color: '#263238',
      border: '1px solid #9ccc65',
      cursor: 'pointer',
    }
  }

  if (dayState.kind === 'no_pass') {
    return {
      ...baseStyle,
      background: '#fff176',
      border: '1px solid #fdd835',
      cursor: 'pointer',
    }
  }

  if (dayState.kind === 'reason') {
    return {
      ...baseStyle,
      background: getReasonStripeBackground(dayState.reason),
      border: '1px solid #98a2b3',
      color: '#475467',
      fontWeight: 'bold',
      cursor: 'pointer',
    }
  }

  if (dayState.kind === 'planned') {
    return {
      ...baseStyle,
      background: '#b3e5fc',
      border: '1px solid #81d4fa',
    }
  }

  return {
    ...baseStyle,
    background: '#ffffff',
  }
}

function groupEmailNotifications(notifications) {
  const groupsByKey = new Map()

  notifications.forEach((item) => {
    const key = [
      item.event_type,
      item.status,
      item.subject || '',
    ].join('|')
    const date = item.sent_at || item.created_at

    if (!groupsByKey.has(key)) {
      groupsByKey.set(key, {
        key,
        eventLabel: item.event_type_display || item.event_type,
        status: item.status,
        statusLabel: item.status_display || item.status,
        date,
        items: [],
        errorCount: 0,
      })
    }

    const group = groupsByKey.get(key)
    group.items.push(item)

    if (item.status === 'ERROR') {
      group.errorCount += 1
    }

    if (date && (!group.date || new Date(date) > new Date(group.date))) {
      group.date = date
    }
  })

  return Array.from(groupsByKey.values())
    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
}

function formatNotificationRecipientSummary(group) {
  const count = group.items.length

  if (count === 1) {
    const item = group.items[0]
    return item.original_recipient_name
      ? `${item.original_recipient_name}${item.original_recipient_email ? ` <${item.original_recipient_email}>` : ''}`
      : item.recipient_email
  }

  return `${count} получателей`
}

function notificationStatusStyle(status) {
  const styles = {
    SENT: {
      background: '#dff0d8',
      color: '#3c763d',
    },
    PENDING: {
      background: '#fcf8e3',
      color: '#8a6d3b',
    },
    ERROR: {
      background: '#f2dede',
      color: '#a94442',
    },
  }

  return {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '22px',
    padding: '0 8px',
    borderRadius: '4px',
    fontWeight: 700,
    ...(styles[status] || {}),
  }
}

function getTodayIso() {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const panelStyle = {
  marginTop: '12px',
  padding: '12px',
  background: '#ffffff',
  border: '1px solid #cfcfcf',
  fontSize: '13px',
}

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '12px',
  marginBottom: '10px',
}

const titleStyle = {
  fontWeight: 700,
  fontSize: '15px',
}

const mutedStyle = {
  color: '#667085',
  marginTop: '4px',
}

const actionsStyle = {
  display: 'flex',
  gap: '8px',
}

const controlsStyle = {
  display: 'flex',
  alignItems: 'end',
  gap: '10px',
  marginBottom: '10px',
}

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  fontWeight: 600,
}

const inputStyle = {
  height: '32px',
  border: '1px solid #b8c0cc',
  borderRadius: '4px',
  padding: '0 8px',
}

const summaryStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '10px',
  marginBottom: '10px',
  background: '#f8fafc',
  border: '1px solid #e4e7ec',
}

const monthSummaryStyle = {
  display: 'flex',
  gap: '16px',
  padding: '10px',
  marginBottom: '10px',
  background: '#eef7ff',
  border: '1px solid #b9dcff',
  fontWeight: 700,
}

const confirmationStyle = (confirmed) => ({
  padding: '10px',
  marginBottom: '10px',
  background: confirmed ? '#edf7ed' : '#fff8e5',
  border: confirmed ? '1px solid #b7dfb9' : '1px solid #f0c36d',
  color: confirmed ? '#1b5e20' : '#7a4f01',
})

const commentsStyle = {
  padding: '10px',
  marginBottom: '10px',
  background: '#fffdf5',
  border: '1px solid #f3df9f',
}

const commentDateStyle = {
  color: '#667085',
}

const notificationsStyle = {
  padding: '10px',
  marginTop: '18px',
  marginBottom: '10px',
  background: '#f8fafc',
  border: '1px solid #d0d5dd',
}

const sectionTitleInlineStyle = {
  fontWeight: 700,
  marginBottom: '8px',
}

const notificationsTableStyle = {
  display: 'grid',
  gridTemplateColumns: '130px minmax(180px, 1fr) minmax(220px, 1.2fr) minmax(120px, 0.7fr) 110px',
  gap: '0',
  fontSize: '12px',
}

const notificationHeaderStyle = {
  padding: '6px 8px',
  background: '#eef2f6',
  border: '1px solid #d0d5dd',
  fontWeight: 700,
}

const notificationRowStyle = {
  display: 'contents',
}

const notificationCellStyle = {
  padding: '6px 8px',
  border: '1px solid #d0d5dd',
  background: '#ffffff',
  minWidth: 0,
}

const notificationMutedStyle = {
  marginTop: '3px',
  color: '#667085',
  fontSize: '11px',
}

const notificationDetailsStyle = {
  marginTop: '6px',
  display: 'grid',
  gap: '6px',
}

const notificationDetailRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '8px',
  paddingTop: '6px',
  borderTop: '1px solid #edf0f5',
}

const notificationErrorStyle = {
  marginTop: '4px',
  color: '#b42318',
  wordBreak: 'break-word',
}

const smallButtonStyle = {
  minHeight: '28px',
  border: '1px solid #9db6d8',
  background: '#f7fbff',
  color: '#1f5f9f',
  borderRadius: '6px',
  padding: '0 10px',
  fontWeight: 600,
  cursor: 'pointer',
}

const sectionTitleStyle = {
  fontWeight: 700,
  margin: 0,
  lineHeight: 1.25,
}

const splitTablesStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 540px) minmax(520px, 1fr)',
  gap: '12px',
}

const splitTableStyle = {
  minWidth: 0,
}

const splitTableTitleStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  minHeight: '44px',
  marginBottom: '6px',
}

const dayPlanGridStyle = {
  height: 260,
  width: '540px',
  maxWidth: '100%',
}

const historyHeaderStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '8px',
  minHeight: '44px',
  marginBottom: '6px',
}

const historySelectStyle = {
  height: '28px',
  border: '1px solid #b8c0cc',
  borderRadius: '4px',
  background: '#ffffff',
  padding: '0 8px',
  fontSize: '12px',
}

const tableDefaultColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  suppressMovable: true,
  suppressHeaderMenuButton: true,
  suppressHeaderFilterButton: true,
  suppressHeaderContextMenu: true,
}

const historyDefaultColDef = {
  sortable: true,
  filter: false,
  resizable: true,
  suppressMovable: true,
  suppressHeaderMenuButton: true,
  suppressHeaderFilterButton: true,
  suppressHeaderContextMenu: true,
}

const primaryButtonStyle = {
  height: '32px',
  padding: '0 12px',
  background: '#1976d2',
  color: '#ffffff',
  border: '1px solid #0d47a1',
  borderRadius: '4px',
  cursor: 'pointer',
}

const secondaryButtonStyle = {
  height: '32px',
  padding: '0 12px',
  background: '#ffffff',
  border: '1px solid #b8c0cc',
  borderRadius: '4px',
  cursor: 'pointer',
}

const dangerButtonStyle = {
  height: '32px',
  padding: '0 12px',
  background: '#b42318',
  color: '#ffffff',
  border: '1px solid #7a271a',
  borderRadius: '4px',
  cursor: 'pointer',
}

const disabledButtonStyle = {
  height: '32px',
  padding: '0 12px',
  background: '#f2f4f7',
  color: '#98a2b3',
  border: '1px solid #d0d5dd',
  borderRadius: '4px',
  cursor: 'not-allowed',
}

const doneTextStyle = {
  alignSelf: 'center',
  color: '#2e7d32',
  fontWeight: 700,
}

const waitingTextStyle = {
  alignSelf: 'center',
  color: '#856404',
  fontWeight: 700,
}

const rejectedTextStyle = {
  alignSelf: 'center',
  color: '#b42318',
  fontWeight: 700,
}

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
}

const modalStyle = {
  width: '420px',
  maxWidth: 'calc(100vw - 32px)',
  background: '#ffffff',
  border: '1px solid #98a2b3',
  borderRadius: '6px',
  padding: '16px',
  boxShadow: '0 12px 30px rgba(15, 23, 42, 0.18)',
}

const textareaStyle = {
  width: '100%',
  maxWidth: '100%',
  minWidth: '100%',
  height: '110px',
  minHeight: '90px',
  maxHeight: '220px',
  marginTop: '12px',
  border: '1px solid #b8c0cc',
  borderRadius: '4px',
  padding: '8px',
  boxSizing: 'border-box',
  resize: 'vertical',
}

const modalLabelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '5px',
  marginTop: '12px',
  fontWeight: 600,
}

const modalInputStyle = {
  height: '32px',
  border: '1px solid #b8c0cc',
  borderRadius: '4px',
  padding: '0 8px',
}

const modalHintStyle = {
  color: '#667085',
  fontWeight: 400,
  fontSize: '12px',
}

const errorStyle = {
  color: '#b42318',
  marginTop: '8px',
}

const modalActionsStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
  marginTop: '12px',
}

const agPopupStyle = `
  .ag-popup,
  .ag-popup-child,
  .ag-menu,
  .ag-filter {
    background: #ffffff !important;
    opacity: 1 !important;
    z-index: 2000 !important;
  }

  .ag-popup-child {
    border: 1px solid #c8d0dc !important;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.22) !important;
  }

  .work-grid {
    font-size: 11px !important;
    --ag-font-size: 11px;
    --ag-row-height: 26px;
    --ag-header-height: 28px;
    scrollbar-gutter: stable;
  }

  .work-grid .ag-body-horizontal-scroll {
    display: flex !important;
    height: 16px !important;
    min-height: 16px !important;
    max-height: 16px !important;
  }

  .work-grid .ag-body-horizontal-scroll-viewport {
    overflow-x: scroll !important;
  }

  .work-grid .ag-body-horizontal-scroll-viewport::-webkit-scrollbar {
    height: 14px;
  }

  .work-grid .ag-body-horizontal-scroll-viewport::-webkit-scrollbar-track {
    background: #eef2f6;
    border-radius: 7px;
  }

  .work-grid .ag-body-horizontal-scroll-viewport::-webkit-scrollbar-thumb {
    background: #98a2b3;
    border: 3px solid #eef2f6;
    border-radius: 7px;
  }

  .work-grid .ag-body-horizontal-scroll-viewport::-webkit-scrollbar-thumb:hover {
    background: #667085;
  }

  .work-grid .ag-cell {
    font-size: 11px !important;
    line-height: 26px !important;
  }

  .work-grid .ag-header-cell-text {
    font-size: 11px !important;
    line-height: 1 !important;
  }

  .well-history-grid .ag-cell {
    padding: 0 !important;
  }

  .well-history-grid .ag-header-cell {
    padding: 0 !important;
  }

  .well-history-grid .graph-day-header .ag-header-cell-label {
    justify-content: center !important;
    padding: 0 !important;
  }

  .well-history-grid .graph-day-header .ag-header-cell-text {
    text-align: center !important;
    width: 100%;
  }

  .well-history-grid .graph-day-cell {
    align-items: center !important;
    justify-content: center !important;
    padding: 0 !important;
    text-align: center !important;
  }

`

export default RequestWorkPanel
